import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function HtmlEmailBody({ html }: { html: string }) {
    const frameRef = useRef<HTMLIFrameElement>(null);
    const cleanupRef = useRef<() => void>(() => {});
    const [height, setHeight] = useState(200);
    const documentHtml = useMemo(() => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('script, iframe, object, embed, base, meta[http-equiv]').forEach(element => element.remove());
        doc.querySelectorAll('a').forEach(link => {
            const href = link.getAttribute('href')?.trim() ?? '';
            if (/^(?:javascript|vbscript|data):/i.test(href)) link.removeAttribute('href');
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });
        const defaults = doc.createElement('style');
        defaults.textContent = 'html { overflow-wrap: anywhere; } body { margin: 0; padding: 0; } img { max-width: 100%; height: auto; }';
        doc.head.prepend(defaults);
        return '<!doctype html>' + doc.documentElement.outerHTML;
    }, [html]);

    useEffect(() => () => cleanupRef.current(), []);
    const onLoad = () => {
        cleanupRef.current();
        const doc = frameRef.current?.contentDocument;
        if (!doc?.body) return;
        let animationFrame: number | null = null;
        const measure = () => {
            if (animationFrame !== null) return;
            animationFrame = window.requestAnimationFrame(() => {
                animationFrame = null;
                const nextHeight = Math.max(40, Math.ceil(Math.max(doc.body.scrollHeight, doc.body.getBoundingClientRect().height)));
                setHeight(current => current === nextHeight ? current : nextHeight);
            });
        };
        const observer = new ResizeObserver(measure);
        observer.observe(doc.body);
        doc.addEventListener('load', measure, true);
        cleanupRef.current = () => {
            observer.disconnect();
            doc.removeEventListener('load', measure, true);
            if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
        };
        measure();
    };
    return (
        <iframe
            ref={frameRef}
            title="Contenuto email"
            srcDoc={documentHtml}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            onLoad={onLoad}
            style={{ display: 'block', border: 0, width: '100%', height }}
        />
    );
}
